import { ObjectCategory } from "@common/constants";
import { type Hitbox } from "@common/utils/hitbox";
import { Buffer, BufferUsage, Container, Geometry, GlProgram, Matrix, Mesh, RendererType, RenderTexture, Shader, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { type Obstacle } from "../objects/obstacle";
import { PIXI_SCALE } from "../utils/constants";
import { CameraManager } from "./cameraManager";
import { type AtlasRead, atlasRead } from "./atlasReadLogic";
import { AtlasReads, canvasWith, readHere } from "./atlasReads";
import { Lighting } from "./lighting";
import { bakeNormals, bakeScale, NORMAL_SCALE, type NormalProfile, NORMALS_FRAGMENT, NORMALS_VERTEX, normalTurn, PROFILES } from "./normalLogic";
import { casterHeight } from "./shadowLogic";

/*
    The shapes of everything on screen, for the lighting (0.98.7, the owner, 1 November 2026: "Lighting that shows
    each object's shape"): every frame, with the lighting at High and the shaders on, a picture of the world's normals
    is drawn (half the screen's pixels each way): the ground's from its materials (war/warShaders.ts, the ground
    shader's normal pass), and over it every thing's own, its picture put down again where it stands with its normal
    map in place of its colours. The grade (war/warShaders.ts) and the lights' relief (managers/lightFans.ts) read it:
    a crate shows a lit side toward the sun and a shaded side away from it, a flash beside it lights its near side, a
    player is a dome, a tree's crown a dome, a roof flat with rounded eaves, a floor flat with the grain of its boards.

    A picture's normal map is baked once, the first time the picture is on screen, from the atlas page as the browser
    holds it (as the snow's caps are: managers/snowLook.ts), by its kind (managers/normalLogic.ts PROFILES), a few a
    frame within a budget; a thing shows no shape for the frames until its map is ready. 0.98.8: baked in the bakes'
    worker (managers/atlasReads.ts), a few at a time, away from the frames: drawn on the page's own thread, a picture
    off a page the browser had let go of had it decode the whole page first, and the game froze for it; the page only
    puts the map's pixels into its texture. Without a worker, the bake is the page's, as it was. The maps are copied into one
    atlas of their own on the card (`ATLAS`), and all the things on screen are one mesh a frame (a quad each, its
    corners where the thing's picture stands, its map's place in the atlas, and how the thing is turned: a normal is a
    direction, so a crate turned a quarter has its lit side turned with it, and a mirrored picture its normals
    mirrored: the mesh's shader turns each). Nothing of it on WebGPU or the Canvas 2D fallback (the lighting's shaders
    are GLSL).
*/

/** the maps' atlas on the card: this many pixels each way, the maps packed in shelves by their height */
export const ATLAS = 2048;

/** the normals' mesh's arrays: `room` quads, `used` of them this frame */
interface QuadArrays {
    room: number
    used: number
    readonly positions: Float32Array
    readonly uvs: Float32Array
    readonly turns: Float32Array
    readonly alphas: Float32Array
    readonly indices: Uint32Array
}

/** a map's place in the atlas (pixels) */
interface Slot { readonly x: number, readonly y: number, readonly w: number, readonly h: number }

export { NORMAL_SCALE } from "./normalLogic";

/** a frame bakes maps until this much of it is spent (one at least) */
const BUDGET_MS = 4;
/** the bakes sent to the worker and not back yet, at most (0.98.8: the rest wait their turn, the ones on screen first) */
const IN_FLIGHT = 4;
/** the atlas is packed again with a frame's maps at most this often (frames) when they don't all fit */
const REPACK_FRAMES = 30;
const TRIES = 3;

/** a sprite that keeps out of the normals' picture (a contact shadow, a marker): managers mark theirs */
type Marked = Sprite & { __noNormal?: boolean };
export function markNoNormal(sprite: Sprite): void {
    (sprite as Marked).__noNormal = true;
}

/** a thing to draw: its sprite, its profile, its place (relative to the camera's container), its alpha, its order */
interface Entry {
    readonly sprite: Sprite
    readonly profile: NormalProfile
    readonly matrix: Matrix
    readonly alpha: number
    readonly order: number
}

/**
 * the atlas page's picture, as the browser can draw it: an image the game loaded (its atlases' pages), never a canvas
 * drawn at run time (an emblem, a snow cap, a hole: made again each match, their maps would pile up, 0.98.7's audit)
 */
type Drawable = CanvasImageSource & { readonly width: number, readonly height: number };
const drawable = (resource: unknown): Drawable | undefined => {
    if (
        (typeof ImageBitmap !== "undefined" && resource instanceof ImageBitmap)
        || (typeof HTMLImageElement !== "undefined" && resource instanceof HTMLImageElement)
    ) return resource as Drawable;
    return undefined;
};

class NormalLookClass {
    private _target?: RenderTexture;
    private readonly _scene = new Container();
    private readonly _ground = new Container();
    /** the flat patches' normal maps (a road's asphalt, a yard's dirt: war/groundMaterials.ts), drawn in world coordinates under the things */
    private readonly _twins = new Container();
    private readonly _things = new Container();
    private readonly _twinOf = new Map<Container, { readonly layer: () => Container | undefined }>();
    private _drawn = false;
    /** the maps' atlas and the places taken in it; the scratch sprite that copies a map in */
    private _atlas?: RenderTexture;
    private readonly _slots = new Map<Texture, Slot>();
    private _shelfY = 0;
    private _shelfX = 0;
    private _shelfH = 0;
    private readonly _copier = new Sprite();
    private readonly _empty = new Container();
    /** this page's frames counted, and the frame the atlas was last packed again on */
    private _frame = 0;
    private _packedAt = -REPACK_FRAMES;
    /** the things' mesh: a quad each */
    private _mesh?: Mesh<Geometry, Shader>;
    private _geometry?: Geometry;
    private _buffers?: { positions: Buffer, uvs: Buffer, turns: Buffer, alphas: Buffer, indices: Buffer };
    /**
     * the mesh's arrays, kept from frame to frame (0.98.7's audit): room for a power of two of quads, 64 at least, grown
     * as it's needed and shrunk when a quarter of it would do; the quads past a frame's own are empty (every corner of
     * their triangles the first: nothing drawn)
     */
    private _arrays: QuadArrays = { room: 0, used: 0, positions: new Float32Array(0), uvs: new Float32Array(0), turns: new Float32Array(0), alphas: new Float32Array(0), indices: new Uint32Array(0) };

    /** the arrays, the quads' count and the buffers last sent to the card (0.98.8) */
    private _sentArrays?: QuadArrays;
    private _sentQuads = -1;
    private _sentBuffers?: { positions: Buffer, uvs: Buffer, turns: Buffer, alphas: Buffer, indices: Buffer };

    private _arraysFor(n: number): QuadArrays {
        let room = 64;
        while (room < n) room *= 2;
        if (room > this._arrays.room || room * 4 <= this._arrays.room) {
            this._arrays = { room, used: 0, positions: new Float32Array(room * 8), uvs: new Float32Array(room * 8), turns: new Float32Array(room * 12), alphas: new Float32Array(room * 4), indices: new Uint32Array(room * 6) };
        }
        const arrays = this._arrays;
        // (the quads drawn last frame past this frame's emptied)
        if (arrays.used > n) arrays.indices.fill(0, n * 6, arrays.used * 6);
        arrays.used = n;
        return arrays;
    }
    /** the maps baked, by picture and profile (`null`: this picture takes none) */
    private readonly _maps = new Map<Texture, Map<NormalProfile, Texture | null>>();
    /** (`here`: the worker couldn't bake it, the page bakes it itself: 0.98.8) */
    private readonly _queue: Array<{ key: string, texture: Texture, profile: NormalProfile, tries: number, here?: boolean }> = [];
    /** the page the last bake sent to the worker was read off (the ones from the same page go together: it's decoded there) */
    private _lastPage?: unknown;
    private readonly _queued = new Set<string>();
    /** the maps the things on screen wait for this frame: baked first */
    private readonly _wanted = new Set<string>();
    private _entries: Entry[] = [];
    private readonly _groundSprites: Container[] = [];
    /** the things drawn into the picture this frame (tests and the lab) */
    private _count = 0;
    get count(): number { return this._count; }
    /** the maps baked so far (the lab) */
    get baked(): number {
        let n = 0;
        for (const maps of this._maps.values()) for (const map of maps.values()) if (map) n++;
        return n;
    }

    constructor() {
        this._scene.addChild(this._ground, this._twins, this._things);
        this._scene.eventMode = "none";
    }

    /**
     * a flat patch's normal twin (a Graphics in world coordinates, drawn with the materials' normal maps): kept and
     * drawn with its floor's container (`layer` says which, each frame) until it's taken away
     */
    addTwin(twin: Container, layer: () => Container | undefined): void {
        if (this._twinOf.has(twin)) return;
        this._twinOf.set(twin, { layer });
        twin.eventMode = "none";
        this._twins.addChild(twin);
    }

    removeTwin(twin: Container): void {
        if (!this._twinOf.delete(twin)) return;
        this._twins.removeChild(twin);
    }

    /** the normals' picture this frame, or none (then everything is lit as flat) */
    get texture(): Texture | undefined { return this._drawn ? this._target : undefined; }

    /** whether the picture is drawn: the lighting at High with the shaders' grade on, on WebGL */
    get active(): boolean {
        return Lighting.rich && Game.gameStarted && Game.pixi.renderer.type === RendererType.WEBGL && this._groundSprites.length > 0;
    }

    /** the ground's normal pass (war/warLook.ts gives it: a full-screen quad with the ground shader's normal variant), or none */
    setGround(source: Container | undefined): void {
        for (const old of this._groundSprites) old.parent?.removeChild(old);
        this._groundSprites.length = 0;
        if (source) {
            this._groundSprites.push(source);
            this._ground.addChild(source);
        }
    }

    /** leaving a match */
    reset(): void {
        this._drawn = false;
        this._count = 0;
        this.setGround(undefined);
        for (const twin of [...this._twinOf.keys()]) this.removeTwin(twin);
    }

    /** the atlas emptied: every map placed again as it's wanted */
    private _clearAtlas(): void {
        this._slots.clear();
        this._shelfX = this._shelfY = this._shelfH = 0;
        if (this._atlas) Game.pixi.renderer.render({ container: this._empty, target: this._atlas, clear: true, clearColor: [0, 0, 0, 0] });
    }

    /** a map's place in the atlas, copied in the first time; none when the atlas has no room left for it */
    private _slot(map: Texture): Slot | undefined {
        const known = this._slots.get(map);
        if (known) return known;
        const renderer = Game.pixi.renderer;
        if (!this._atlas) {
            this._atlas = RenderTexture.create({ width: ATLAS, height: ATLAS, resolution: 1, scaleMode: "linear" });
            renderer.render({ container: this._empty, target: this._atlas, clear: true, clearColor: [0, 0, 0, 0] });
        }
        const w = Math.ceil(map.orig.width), h = Math.ceil(map.orig.height);
        if (w > ATLAS || h > ATLAS) return undefined;
        const pad = 2;
        let x = this._shelfX, y = this._shelfY, shelf = this._shelfH;
        if (x + w + pad > ATLAS) {
            x = 0;
            y += shelf + pad;
            shelf = 0;
        }
        if (y + h + pad > ATLAS) return undefined;
        this._shelfX = x;
        this._shelfY = y;
        this._shelfH = shelf;
        const slot: Slot = { x: this._shelfX, y: this._shelfY, w, h };
        this._shelfX += w + pad;
        this._shelfH = Math.max(this._shelfH, h);
        const copier = this._copier;
        copier.texture = map;
        copier.anchor.set(0);
        copier.position.set(slot.x, slot.y);
        copier.scale.set(1);
        renderer.render({ container: copier, target: this._atlas, clear: false });
        this._slots.set(map, slot);
        return slot;
    }

    /** the things' mesh, made once (its buffers filled each frame) */
    private _meshOf(): Mesh<Geometry, Shader> {
        if (this._mesh && this._geometry && this._buffers && this._atlas) {
            const shader = this._mesh.shader;
            if (shader) shader.resources.uAtlas = this._atlas.source;
            return this._mesh;
        }
        const make = (size: number, index = false): Buffer => new Buffer({ data: index ? new Uint32Array(size) : new Float32Array(size), usage: (index ? BufferUsage.INDEX : BufferUsage.VERTEX) | BufferUsage.COPY_DST });
        const buffers = this._buffers = { positions: make(8), uvs: make(8), turns: make(12), alphas: make(4), indices: make(6, true) };
        const geometry = this._geometry = new Geometry({
            attributes: { aPosition: buffers.positions, aUV: buffers.uvs, aTurn: buffers.turns, aAlpha: buffers.alphas },
            indexBuffer: buffers.indices
        });
        const shader = new Shader({
            glProgram: GlProgram.from({ vertex: NORMALS_VERTEX, fragment: NORMALS_FRAGMENT, name: "normal-look" }),
            resources: { uAtlas: (this._atlas ?? Texture.EMPTY).source }
        });
        const mesh = this._mesh = new Mesh({ geometry, shader });
        mesh.eventMode = "none";
        this._things.addChild(mesh);
        return mesh;
    }

    /**
     * a picture's normal map (by its profile), baked already, or none yet (then it's queued; `null` when the picture
     * can't be baked: not from an atlas page, or too small)
     */
    private _mapOf(texture: Texture, profile: NormalProfile): Texture | null | undefined {
        let maps = this._maps.get(texture);
        if (!maps) {
            maps = new Map();
            this._maps.set(texture, maps);
            // (the picture gone, its maps go: their place in the atlas and their textures)
            texture.once("destroy", () => this._forget(texture));
        }
        const known = maps.get(profile);
        if (known !== undefined) return known;
        const key = `${texture.uid}:${profile.shape}:${profile.bevel}`;
        this._wanted.add(key);
        if (!this._queued.has(key)) {
            this._queued.add(key);
            this._queue.push({ key, texture, profile, tries: 0 });
        }
        return undefined;
    }

    /** a picture's maps forgotten (it was destroyed): their textures freed, their places in the atlas given up */
    private _forget(texture: Texture): void {
        const maps = this._maps.get(texture);
        this._maps.delete(texture);
        for (let i = this._queue.length - 1; i >= 0; i--) {
            const job = this._queue[i];
            if (job.texture !== texture) continue;
            this._queued.delete(job.key);
            this._queue.splice(i, 1);
        }
        if (!maps) return;
        for (const map of maps.values()) {
            if (!map) continue;
            this._slots.delete(map);
            map.destroy(true);
        }
    }

    /**
     * a few of the maps waiting, baked, within the frame's budget: sent to the bakes' worker when it can read the
     * picture's page (0.98.8: a few at a time, those of the page last sent first among those on screen), baked here
     * otherwise
     */
    private _bakeSome(): void {
        if (this._queue.length === 0) return;
        const started = performance.now();
        for (let n = 0; this._queue.length > 0 && (n === 0 || performance.now() - started < BUDGET_MS); n++) {
            // (a picture on screen before one that has left it; among them, one off the page the worker has now)
            let at = this._queue.findIndex(job => this._wanted.has(job.key) && job.texture.source?.resource === this._lastPage);
            if (at < 0) at = this._queue.findIndex(job => this._wanted.has(job.key));
            if (at < 0) at = 0;
            const next = this._queue[at];
            const page = next.texture.source?.resource;
            if (!next.here && AtlasReads.canRead(page)) {
                if (AtlasReads.busy >= IN_FLIGHT) break;
                this._queue.splice(at, 1);
                this._send(next, page);
                continue;
            }
            const job = this._queue.splice(at, 1)[0];
            if (!job) break;
            let map: Texture | null | undefined;
            try {
                map = this._bake(job.texture, job.profile);
            } catch (e) {
                if (++job.tries < TRIES) {
                    this._queue.push(job);
                    continue;
                }
                console.warn("normals: a map couldn't be baked", job.texture.label, e);
                map = null;
            }
            this._maps.get(job.texture)?.set(job.profile, map ?? null);
        }
    }

    /**
     * how a picture is read for its map: its page, and its frame cut out of it, turned back as the packer keeps it, at
     * the bake's size (managers/atlasReadLogic.ts); none when it can't be (destroyed, too small, not off an atlas page,
     * turned other than the packer turns)
     */
    private _plan(texture: Texture): { readonly page: Drawable, readonly read: AtlasRead } | undefined {
        if (texture.destroyed) return undefined;
        const width = texture.orig.width, height = texture.orig.height;
        if (width < 4 || height < 4) return undefined;
        const page = drawable(texture.source.resource);
        if (!page) return undefined;
        const rotate = texture.rotate;
        if (rotate !== 0 && rotate !== 2) return undefined;
        const k = bakeScale(width, height);
        const w = Math.max(4, Math.round(width * k)), h = Math.max(4, Math.round(height * k));
        return { page, read: atlasRead({ frame: texture.frame, trim: texture.trim, rotate, resolution: texture.source.resolution || 1 }, width, height, w, h) };
    }

    /** the map's texture from its canvas */
    private _texture(canvas: HTMLCanvasElement): Texture {
        // (its own texture, out of Pixi's cache: freed with the picture it was baked from, `_forget`)
        const map = Texture.from(canvas, true);
        // (a normal is a colour, the alpha its cover: premultiplied as it's drawn, so edges blend into what's under them)
        map.source.scaleMode = "linear";
        return map;
    }

    /**
     * a bake sent to the worker (0.98.8): its map set when its pixels come back (unless its picture was forgotten
     * meanwhile); when the worker can't, back in the queue to be baked here
     */
    private _send(job: { key: string, texture: Texture, profile: NormalProfile, tries: number, here?: boolean }, page: HTMLImageElement): void {
        const { texture, profile } = job;
        const plan = this._plan(texture);
        if (!plan) {
            this._maps.get(texture)?.set(profile, null);
            return;
        }
        this._lastPage = page;
        const { w, h } = plan.read;
        AtlasReads.bake(page, { kind: "normal", read: plan.read, profile }).then(pixels => {
            const maps = this._maps.get(texture);
            if (!maps || texture.destroyed) return;
            const canvas = pixels ? canvasWith(pixels, w, h) : undefined;
            maps.set(profile, canvas ? this._texture(canvas) : null);
        }, () => {
            if (!this._maps.has(texture) || texture.destroyed) return;
            job.here = true;
            this._queue.push(job);
        });
    }

    /** the map baked here from the picture: its frame cut out of its atlas page, turned back as the packer keeps it */
    private _bake(texture: Texture, profile: NormalProfile): Texture | null {
        const plan = this._plan(texture);
        if (!plan) return null;
        const here = readHere(plan.page, plan.read);
        if (!here) return null;
        const { w, h } = plan.read;
        const baked = bakeNormals(here.data.data, w, h, profile);
        const out = here.ctx.createImageData(w, h);
        out.data.set(baked);
        here.ctx.putImageData(out, 0, 0);
        return this._texture(here.canvas);
    }

    /** how far a kind of thing reaches from its middle (world pixels), by its hitbox's corners: kept by its name */
    private readonly _reaches = new Map<string, number>();
    private _reachOf(idString: string, hitbox: Hitbox | undefined): number {
        let reach = this._reaches.get(idString);
        if (reach === undefined) {
            const box = hitbox?.toRectangle();
            reach = box ? Math.hypot(Math.max(Math.abs(box.min.x), Math.abs(box.max.x)), Math.max(Math.abs(box.min.y), Math.abs(box.max.y))) * PIXI_SCALE : 0;
            this._reaches.set(idString, reach);
        }
        return reach;
    }

    /** the profile an obstacle's picture is baked with */
    private _obstacleProfile(obstacle: Obstacle, sprite: Sprite): NormalProfile {
        const definition = obstacle.definition;
        if (obstacle.dead) return PROFILES.flat;
        const height = casterHeight(definition);
        if (height <= 0) return PROFILES.flat;
        if (definition.isTree) return sprite === obstacle.leavesSprite ? PROFILES.round : PROFILES.thing;
        if (definition.material === "bush") return PROFILES.round;
        if (definition.isWall || definition.wall) return PROFILES.wall;
        return PROFILES.thing;
    }

    private readonly _matrix = new Matrix();
    /** the things' matrices, kept from frame to frame (one per thing drawn: `_matrixAt`) */
    private readonly _matrixPool: Matrix[] = [];
    private _matrices = 0;

    private _matrixAt(index: number): Matrix {
        let matrix = this._matrixPool[index];
        if (!matrix) this._matrixPool[index] = matrix = new Matrix();
        return matrix;
    }

    /**
     * a container's visible sprites gathered, each with its place in the world (pixels, relative to the camera
     * container), by `profile` (or `profileOf` the sprite); `alpha` and `matrix` the container's own
     */
    private _gather(container: Container, matrix: Matrix, alpha: number, order: number, profileOf: (sprite: Sprite) => NormalProfile): void {
        if (container.sortableChildren && container.sortDirty) container.sortChildren();
        for (const child of container.children) {
            if (!child.visible || child.alpha <= 0.005) continue;
            child.updateLocalTransform();
            const m = this._matrixAt(this._matrices++).copyFrom(matrix).append(child.localTransform);
            const a = alpha * child.alpha;
            if (child instanceof Sprite) {
                const marked = child as Marked;
                const texture = child.texture;
                if (!marked.__noNormal && texture && !texture.destroyed && texture.source && drawable(texture.source.resource)) {
                    this._entries.push({ sprite: child, profile: profileOf(child), matrix: m, alpha: a, order });
                }
            }
            if (child.children.length > 0) this._gather(child, m, a, order, profileOf);
        }
    }

    /** every frame, after the lighting and before the shaders' grade: the picture drawn */
    update(): void {
        this._count = 0;
        this._frame++;
        this._matrices = 0;
        if (!this.active) {
            if (this._mesh) this._mesh.visible = false;
            this._drawn = false;
            return;
        }
        const renderer = Game.pixi.renderer;
        const screen = Game.pixi.screen;
        const width = Math.max(1, Math.ceil(screen.width * NORMAL_SCALE)), height = Math.max(1, Math.ceil(screen.height * NORMAL_SCALE));
        if (!this._target) {
            this._target = RenderTexture.create({ width, height, resolution: 1, scaleMode: "linear", dynamic: true });
        } else if (this._target.width !== width || this._target.height !== height) {
            this._target.resize(width, height);
        }
        // the ground's normals: the quad covers the picture (a sprite of a one-pixel texture: its width and height are
        // its scale, set as the picture's size: 0.98.7's audit, a scale of half the screen's drew it half a pixel big)
        for (const ground of this._groundSprites) {
            ground.position.set(0, 0);
            ground.width = width;
            ground.height = height;
        }
        // the things: the camera's view, at the picture's scale (as the shadows' is: managers/shadowLook.ts)
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const k = NORMAL_SCALE;
        this._things.scale.set(scale * k, scale * k);
        this._things.position.set(offset.x * k, offset.y * k);
        this._twins.scale.set(scale * k, scale * k);
        this._twins.position.set(offset.x * k, offset.y * k);
        for (const [twin, { layer }] of this._twinOf) {
            const container = layer();
            const alpha = container?.visible ? container.alpha : 0;
            twin.visible = alpha > 0.01;
            twin.alpha = alpha;
        }
        const left = -offset.x / scale, top = -offset.y / scale;
        const w = screen.width / scale, h = screen.height / scale;
        const margin = 400;

        this._entries = [];
        this._wanted.clear();
        const layerOrder = (container: Container | undefined): number => container ? CameraManager.layerContainers.indexOf(container) : -1;
        const shown = (container: Container | undefined): number => container?.visible ? container.alpha : 0;
        const gather = (objectContainer: Container, layerContainer: Container | undefined, profileOf: (sprite: Sprite) => NormalProfile, reach = 0): void => {
            const alpha = shown(layerContainer);
            if (alpha <= 0.01 || !objectContainer.visible) return;
            // (only what's near the screen: a thing's middle within the margin and its own reach of it)
            const px = objectContainer.position.x, py = objectContainer.position.y;
            const near = margin + reach;
            if (px < left - near || px > left + w + near || py < top - near || py > top + h + near) return;
            objectContainer.updateLocalTransform();
            const order = layerOrder(layerContainer) * 10000 + objectContainer.zIndex;
            this._gather(objectContainer, this._matrix.copyFrom(objectContainer.localTransform), alpha * objectContainer.alpha, order, profileOf);
        };
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            if (obstacle.destroyed) continue;
            gather(obstacle.container, obstacle.layerContainer, sprite => this._obstacleProfile(obstacle, sprite), this._reachOf(obstacle.definition.idString, obstacle.definition.spawnHitbox ?? obstacle.definition.hitbox));
        }
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            if (building.destroyed) continue;
            const reach = this._reachOf(building.definition.idString, building.definition.spawnHitbox);
            gather(building.container, building.layerContainer, () => PROFILES.flat, reach);
            gather(building.ceilingContainer, building.layerContainer, () => PROFILES.roof, reach);
        }
        for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
            if (player.destroyed || player.air) continue;
            const body = player.images.body;
            gather(player.container, player.layerContainer, sprite => (sprite === body ? PROFILES.round : PROFILES.small));
        }
        for (const animal of Game.objects.getCategory(ObjectCategory.Animal)) {
            if (animal.destroyed) continue;
            gather(animal.container, animal.layerContainer, () => PROFILES.round);
        }
        for (const loot of Game.objects.getCategory(ObjectCategory.Loot)) {
            if (loot.destroyed) continue;
            gather(loot.container, loot.layerContainer, () => PROFILES.small);
        }
        this._entries.sort((a, b) => a.order - b.order);
        // the mesh: a quad for every thing whose map is baked, each map placed in the atlas first; when this frame's
        // don't all fit, the atlas is packed again with this frame's alone (at most every REPACK_FRAMES frames: those
        // still left out are drawn flat), never cleared under quads already made (0.98.7's audit)
        const ready: Array<{ entry: Entry, map: Texture }> = [];
        for (const entry of this._entries) {
            const map = this._mapOf(entry.sprite.texture, entry.profile);
            if (map) ready.push({ entry, map });
        }
        const place = (): Array<{ entry: Entry, slot: Slot }> => {
            const placed: Array<{ entry: Entry, slot: Slot }> = [];
            for (const { entry, map } of ready) {
                const slot = this._slot(map);
                if (slot) placed.push({ entry, slot });
            }
            return placed;
        };
        let quads = place();
        if (quads.length < ready.length && this._frame - this._packedAt >= REPACK_FRAMES) {
            this._packedAt = this._frame;
            this._clearAtlas();
            quads = place();
        }
        if (quads.length > 0 && this._atlas) {
            const mesh = this._meshOf();
            const n = quads.length;
            const arrays = this._arraysFor(n);
            const { positions, uvs, turns, alphas, indices } = arrays;
            // (0.98.8: each value compared with what was there as it's written, and only the buffers with a value
            // changed sent to the card again: the camera moves the quads, but their maps, turns and shades stay)
            let movedP = false, movedU = false, movedT = false, movedA = false, was = 0;
            for (let i = 0; i < n; i++) {
                const { entry, slot } = quads[i];
                const m = entry.matrix;
                const orig = entry.sprite.texture.orig;
                const ax = entry.sprite.anchor.x * orig.width, ay = entry.sprite.anchor.y * orig.height;
                // the thing's turn (its picture's, with the camera's and its parents'): a normal turns with it; a
                // mirrored picture (a negative scale) has its normals mirrored (normalLogic.ts `normalTurn`)
                const { cos, sin, flip } = normalTurn(m.a, m.b, m.c, m.d);
                for (let k = 0; k < 4; k++) {
                    // (the corners round: top left, top right, bottom right, bottom left)
                    const right = k === 1 || k === 2, bottom = k >= 2;
                    const cx = right ? orig.width - ax : -ax, cy = bottom ? orig.height - ay : -ay;
                    const p = i * 8 + k * 2, t = i * 12 + k * 3, a = i * 4 + k;
                    was = positions[p]; positions[p] = m.a * cx + m.c * cy + m.tx; if (positions[p] !== was) movedP = true;
                    was = positions[p + 1]; positions[p + 1] = m.b * cx + m.d * cy + m.ty; if (positions[p + 1] !== was) movedP = true;
                    was = uvs[p]; uvs[p] = (right ? slot.x + slot.w : slot.x) / ATLAS; if (uvs[p] !== was) movedU = true;
                    was = uvs[p + 1]; uvs[p + 1] = (bottom ? slot.y + slot.h : slot.y) / ATLAS; if (uvs[p + 1] !== was) movedU = true;
                    was = turns[t]; turns[t] = cos; if (turns[t] !== was) movedT = true;
                    was = turns[t + 1]; turns[t + 1] = sin; if (turns[t + 1] !== was) movedT = true;
                    was = turns[t + 2]; turns[t + 2] = flip; if (turns[t + 2] !== was) movedT = true;
                    was = alphas[a]; alphas[a] = entry.alpha; if (alphas[a] !== was) movedA = true;
                }
                // (a quad's corners are its place's: the same at the same place, frame after frame)
                const v = i * 4, o = i * 6;
                indices[o] = v; indices[o + 1] = v + 1; indices[o + 2] = v + 2;
                indices[o + 3] = v; indices[o + 4] = v + 2; indices[o + 5] = v + 3;
            }
            const buffers = this._buffers;
            if (buffers) {
                // (all of them when the arrays or the buffers are new, or the quads' count changed: the room past
                // the quads emptied with it)
                const fresh = arrays !== this._sentArrays || n !== this._sentQuads || buffers !== this._sentBuffers;
                if (fresh || movedP) buffers.positions.data = positions;
                if (fresh || movedU) buffers.uvs.data = uvs;
                if (fresh || movedT) buffers.turns.data = turns;
                if (fresh || movedA) buffers.alphas.data = alphas;
                if (fresh) buffers.indices.data = indices;
                this._sentArrays = arrays;
                this._sentQuads = n;
                this._sentBuffers = buffers;
            }
            mesh.visible = true;
            this._count = n;
        } else if (this._mesh) {
            // (0.98.8: hidden only on a frame without it; hidden and shown again each frame, Pixi built the picture's
            // draw list again each frame)
            this._mesh.visible = false;
        }
        this._bakeSome();
        renderer.render({ container: this._scene, target: this._target, clear: true, clearColor: [0.5, 0.5, 1, 1] });
        this._drawn = true;
    }
}

export const NormalLook = new NormalLookClass();
